function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect, useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { grey10, white, boxShadowOverlaid, borderRadius } from "@churchofjesuschrist/eden-style-constants";
import { useClickBoundary } from "@churchofjesuschrist/eden-click-boundary";
import { createPopper } from "@popperjs/core";
const StyledCallout = styled.dialog.withConfig({
  componentId: "sc-yoesbz-0"
})(["--calloutBackgroundColor:", ";--calloutBorderColor:", ";position:absolute;background-color:var(--calloutBackgroundColor);border:1px solid var(--calloutBorderColor);border-radius:", ";box-shadow:", ";max-width:19rem;opacity:0;z-index:3;padding:0;margin:0;&:before{display:block;content:\"\";height:11px;top:-11px;width:100%;position:absolute;[data-popper-placement^=\"top\"]&{top:auto;bottom:-11px;}", "}&[data-popper-placement]{opacity:1;}"], white, grey10, borderRadius, boxShadowOverlaid, _ref => {
  let {
    arrow
  } = _ref;
  return arrow === "overlay" ? "pointer-events: none;" : "";
});
const Arrow = styled.div.withConfig({
  componentId: "sc-yoesbz-1"
})(["position:absolute;&:before,&:after{", " border:solid transparent;content:\" \";height:0;width:0;position:absolute;z-index:4;bottom:100%;left:50%;}[data-popper-placement] &{bottom:0;&:before,&:after{top:100%;}&:after{border-top-color:var(--calloutBackgroundColor);border-width:9px;margin-left:-8px;border-bottom-width:0;}&:before{border-top-color:var(--calloutBorderColor);border-width:11px;margin-left:-10px;border-bottom-width:0;}}[data-popper-placement^=\"bottom\"] &{bottom:100%;&:after,&:before{transform:rotate(-180deg);bottom:0;top:auto;}}"], _ref2 => {
  let {
    arrow
  } = _ref2;
  return arrow === "overlay" ? "pointer-events: none;" : "";
});
const Callout = _ref3 => {
  let {
    forRef,
    open = false,
    arrow = "normal",
    onClickOutside,
    children,
    ...props
  } = _ref3;
  const [internalOpen, setInternalOpen] = useState(false);
  const arrowRef = useRef(null);
  const onClickOutsideInternal = useCallback(event => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(event);
    });
  }, [open, onClickOutside]);
  const callout = useClickBoundary({
    onClickInside: x => x,
    onClickOutside: onClickOutsideInternal
  });
  useEffect(() => {
    if (open) {
      const handleEsc = event => {
        if (event.code === "Escape") {
          onClickOutsideInternal(event);
        }
      };
      document.addEventListener("keyup", handleEsc, {
        capture: true
      });
      return () => document.removeEventListener("keyup", handleEsc, {
        capture: true
      });
    }
  }, [open, onClickOutsideInternal]);
  useEffect(() => {
    if (forRef.current && callout.current && internalOpen) {
      const popperInstance = createPopper(forRef.current, callout.current, arrow === "none" ? {
        placement: "bottom"
      } : {
        placement: "bottom",
        modifiers: [{
          name: "arrow",
          options: {
            element: arrowRef.current
          }
        }, {
          name: "offset",
          options: {
            ...(arrow === "overlay" ? {
              offset: [0, 0]
            } : {
              offset: [0, 10]
            })
          }
        }]
      });
      return () => {
        popperInstance.destroy();
      };
    }
  }, [callout, forRef, internalOpen, arrow]);
  useEffect(() => {
    if (callout.current && open) {
      var _callout$current$show, _callout$current;
      (_callout$current$show = (_callout$current = callout.current).show) === null || _callout$current$show === void 0 ? void 0 : _callout$current$show.call(_callout$current);
      setInternalOpen(true);
      return () => {
        setInternalOpen(false);
      };
    }
  }, [callout, open]);
  return open ? /*#__PURE__*/React.createElement(StyledCallout, _extends({
    arrow: arrow
  }, props, {
    ref: callout
  }), arrow === "none" ? null : /*#__PURE__*/React.createElement(Arrow, {
    arrow: arrow,
    ref: arrowRef
  }), internalOpen ? children : null) : null;
};
Callout.propTypes = {
  /** The ref to which the callout belongs */
  forRef: PropTypes.object.isRequired,
  /** Content inside callout */
  children: PropTypes.node.isRequired,
  /** Whether the callout is open */
  open: PropTypes.bool,
  /** Whether the arrow is hidden */
  arrow: PropTypes.oneOf(["none", "normal", "overlay"]),
  /** Handler for when a click occurs outside of the callout (e.g., to close the callout) */
  onClickOutside: PropTypes.func
};
export default Callout;